import { z } from 'zod';
import { HARVEST_PHASES } from '../constants/phases.constants';
import { CULTURES } from '../constants/cultures.constants';
import { CROP_STAGES } from '../constants/cropStages.constants';
import { NUTRIENTS, NUTRIENT_NAMES } from '../constants/nutrients.constants';

const FIXED_PARCEL_TOTAL = 100;
const FLOATING_POINT_TOLERANCE = 1e-9;

const hasAtMostOneDecimalPlace = (value: number) =>
	Math.abs(Math.round(value * 10) - value * 10) < 1e-6;

const nutrientArraySchema = z
	.array(
		z.number({ error: 'Preencha todos os valores dos nutrientes' })
			.min(0, 'Valor não pode ser negativo')
			.max(100, 'Valor não pode ser maior que 100')
			.refine(hasAtMostOneDecimalPlace, 'Valor deve ter no máximo uma casa decimal')
	)
	.length(12, 'Deve conter exatamente 12 valores (um por mês)');

const phaseArraySchema = z
	.array(z.enum(HARVEST_PHASES, { error: 'Selecione uma fase válida para cada mês' }))
	.length(12, 'Deve conter exatamente 12 fases (uma por mês)');

export const parcelDataSchema = z
	.object({
		phase: phaseArraySchema,
		N: nutrientArraySchema,
		P: nutrientArraySchema,
		K: nutrientArraySchema,
		Ca: nutrientArraySchema,
		Mg: nutrientArraySchema,
		S: nutrientArraySchema,
		B: nutrientArraySchema,
		Cu: nutrientArraySchema,
		Fe: nutrientArraySchema,
		Mn: nutrientArraySchema,
		Zn: nutrientArraySchema,
	})
	.superRefine((data, ctx) => {
		NUTRIENTS.forEach((nutrient) => {
			const values = data[nutrient];
			if (values.some((value) => Number.isNaN(value))) return;

			const total = values.reduce((sum, value) => sum + value, 0);
			if (Math.abs(total - FIXED_PARCEL_TOTAL) > FLOATING_POINT_TOLERANCE) {
				ctx.addIssue({
					code: 'custom',
					path: [nutrient],
					message: `O total de ${NUTRIENT_NAMES[nutrient]} deve somar 100% nos 12 meses (atual: ${total.toFixed(1)}%).`,
				});
			}
		});
	});

export const parcellingSchema = z.object({
	name: z
		.string()
		.trim()
		.min(3, 'Identificação deve ter no mínimo 3 caracteres')
		.max(100, 'Identificação deve ter no máximo 100 caracteres'),

	culture: z.enum(CULTURES, { error: 'Selecione uma cultura válida' }),

	cropStage: z.enum(CROP_STAGES, { error: 'Selecione um estágio de cultura válido' }),

	note: z
		.string()
		.max(500, 'Observação deve ter no máximo 500 caracteres')
		.optional()
		.or(z.literal('')),

	bindingUserIds: z.array(z.string()).default([]),

	farmIds: z.array(z.string()).default([]),

	isPublic: z.boolean().default(false),

	parcels: parcelDataSchema,
});

export const createParcellingSchema = parcellingSchema;

export const updateParcellingSchema = parcellingSchema.partial().extend({
	_id: z.string().min(1, 'ID é obrigatório'),
});

export type ParcellingFormData = z.infer<typeof parcellingSchema>;
export type CreateParcellingData = z.infer<typeof createParcellingSchema>;
export type UpdateParcellingData = z.infer<typeof updateParcellingSchema>;
